import axios from 'axios'

const DEFAULT_TIMEOUT_MS = 15_000

// Activity Stream API - runs on same server as UI (Spring Boot)
const DEFAULT_ACTIVITY_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ACTIVITY_API_BASE_URL) || '/api/activity'

// CEP API - dedicated router that routes to customer-specific instances based on API key
// Handles: statements, metrics, statement streams (/api/monitor)
const DEFAULT_CEP_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CEP_API_BASE_URL) || '/api'

let authStateSelector = () => ({
  apiKey: '',
  writeKey: '',
})

// CEP API client - for statements, metrics, and CEP operations
const cepApiClient = axios.create({
  baseURL: DEFAULT_CEP_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
})

// Activity Stream API client - for semantic activity events
const activityApiClient = axios.create({
  baseURL: DEFAULT_ACTIVITY_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
})

// Default client for backward compatibility (points to CEP)
const apiClient = cepApiClient

const shouldAttachWriteKey = (config) =>
  config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())

// Request interceptor - attach auth headers
const requestInterceptor = (config) => {
  const { apiKey, writeKey } = authStateSelector() ?? {}
  const headers = {
    ...(config.headers ?? {}),
  }

  if (apiKey) {
    headers['x-api-key'] = apiKey
  } else {
    delete headers['x-api-key']
  }

  if (writeKey && shouldAttachWriteKey(config)) {
    headers.writekey = writeKey
  } else {
    delete headers.writekey
  }

  config.headers = headers
  return config
}

// Response interceptor - normalize errors
const responseInterceptor = (response) => response
const errorInterceptor = (error) => {
  if (error.response) {
    const normalized = {
      status: error.response.status,
      message: error.response.data?.message ?? error.message,
      data: error.response.data,
      headers: error.response.headers,
    }
    error.normalized = normalized
  } else if (error.request) {
    error.normalized = {
      status: 'NETWORK_ERROR',
      message: 'Request could not be completed. Check your connection and retry.',
    }
  } else {
    error.normalized = {
      status: 'CLIENT_ERROR',
      message: error.message,
    }
  }
  return Promise.reject(error)
}

// Apply interceptors to both clients
cepApiClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error))
cepApiClient.interceptors.response.use(responseInterceptor, errorInterceptor)

activityApiClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error))
activityApiClient.interceptors.response.use(responseInterceptor, errorInterceptor)

export const bindApiClientToStore = (store) => {
  authStateSelector = () => store.getState().auth
  return () => {
    authStateSelector = () => ({
      apiKey: '',
      writeKey: '',
    })
  }
}

export const setApiBaseUrl = (baseURL) => {
  cepApiClient.defaults.baseURL = baseURL
}

export const setCepApiBaseUrl = (baseURL) => {
  cepApiClient.defaults.baseURL = baseURL
}

export const setActivityApiBaseUrl = (baseURL) => {
  activityApiClient.defaults.baseURL = baseURL
}

export const setRequestTimeout = (timeoutMs) => {
  cepApiClient.defaults.timeout = timeoutMs
  activityApiClient.defaults.timeout = timeoutMs
}

// Default exports use CEP client (for statements/metrics services)
export const get = async (url, config) => {
  const response = await cepApiClient.get(url, config)
  return response.data
}

export const post = async (url, payload, config) => {
  const response = await cepApiClient.post(url, payload, config)
  return response.data
}

export const del = async (url, config) => {
  const response = await cepApiClient.delete(url, config)
  return response.data
}

// Export both clients for direct access if needed
export { cepApiClient, activityApiClient }

export default apiClient
